import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDate, errMsg } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Monitor } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/login-history").then((r) => setHistory(r.data));
  }, []);

  const changePw = async (e) => {
    e.preventDefault();
    if (nw !== confirm) return toast.error("New passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw(""); setConfirm("");
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="overline">Account</p>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1 mb-8">Profile &amp; Security</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-sm p-6">
            <h2 className="font-heading font-bold text-lg mb-4">Profile</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-border/40 pb-3"><span className="text-muted-foreground">Name</span><span className="font-medium" data-testid="profile-name">{user?.name}</span></div>
              <div className="flex justify-between border-b border-border/40 pb-3"><span className="text-muted-foreground">Email</span><span className="font-mono-num text-xs">{user?.email}</span></div>
              <div className="flex justify-between border-b border-border/40 pb-3"><span className="text-muted-foreground">Phone</span><span className="font-mono-num text-xs">{user?.phone || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="text-[#059669] font-medium capitalize">{user?.status}</span></div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-6">
            <h2 className="font-heading font-bold text-lg mb-4">Change Password</h2>
            <form onSubmit={changePw} className="space-y-4">
              <div>
                <Label className="overline">Current Password</Label>
                <Input data-testid="cur-password" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className="mt-2 rounded-sm h-11" />
              </div>
              <div>
                <Label className="overline">New Password</Label>
                <Input data-testid="new-password" type="password" required value={nw} onChange={(e) => setNw(e.target.value)} className="mt-2 rounded-sm h-11" />
              </div>
              <div>
                <Label className="overline">Confirm New Password</Label>
                <Input data-testid="confirm-password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 rounded-sm h-11" />
              </div>
              <Button data-testid="change-password-submit" type="submit" disabled={loading} className="w-full h-11 rounded-sm">
                {loading ? "Updating…" : "Update Password"}
              </Button>
            </form>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Login History</h2>
          <div className="space-y-3" data-testid="login-history">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">No login records.</p>
            ) : history.map((h) => (
              <div key={h.id} className="flex items-start gap-3 border-b border-border/40 pb-3 last:border-0">
                <Monitor size={16} className="text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{fmtDate(h.at)}</p>
                  <p className="text-xs text-muted-foreground font-mono-num">IP: {h.ip}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{h.user_agent}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
